import { Injectable } from '@angular/core';

import {
  IBrandLogo, IPushCard, IPushCardTracking, IPushCardWithLayout, PositionEnum, PushSizeEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { IMiniPushCard } from '@seb/web-design-system/mini-push-card';
import { ISeoParagraph } from '@seb/web-design-system/seos';

import {
  CategoryAdditionalMarketingData, CustomMedia, ExtendedIMedia, PushCardComponentInterface,
  PushCardMediaConfigEnum, PushCardSizeEnum, pushCardWithLayoutSkeleton
} from '../models';

import { CategoryAdditionalMarketingDataTargetEnum } from '../enums';

import { HobStateService } from '../services/hob-state.service';
import { ImageService } from '../services/image.service';

import { transformHeadlineLevelToTitleLevel } from './title-level.mapper';
import { HeadingParagraphMapper } from './headingParagraph.mapper';
import { ImageRatioMapper } from './image-ratio.mapper';
import { ThemesMapper } from './themes.mapper';
import { toHeading } from './heading.mapper';
import { LinkMapper } from './link.mapper';

@Injectable({
  providedIn: 'root'
})
export class PushCardMapper {
  constructor(
    private readonly imageService: ImageService,
    private readonly headingParagraphMapper: HeadingParagraphMapper,
    private readonly linkMapper: LinkMapper,
    private readonly hobStateService: HobStateService
  ) {}

  mapPushCardSize(pushCardSize: PushCardSizeEnum): PushSizeEnum {
    switch (pushCardSize) {
      case PushCardSizeEnum.L:
        return PushSizeEnum.l;
      case PushCardSizeEnum.M:
        return PushSizeEnum.m;
      case PushCardSizeEnum.S:
        return PushSizeEnum.s;
      case PushCardSizeEnum.XS:
        return PushSizeEnum.xs;
      case PushCardSizeEnum.XXS:
        return PushSizeEnum.xxs;
      default:
        return PushSizeEnum.l;
    }
  }

  toPushCardWithLayoutSkeleton(cmp: PushCardComponentInterface): IPushCardWithLayout {
    return {
      ...pushCardWithLayoutSkeleton,
      mediaConfig: ImageRatioMapper.mapPosition(cmp.mediaConfig),
      mediaAsBackground: cmp.mediaConfig === PushCardMediaConfigEnum.BACKGROUND,
      imageRatio: ImageRatioMapper.mapImageRatio(cmp.imageRatio)
    };
  }

  toPushCardWithLayout(
    cmp: PushCardComponentInterface,
    mediaContainer?: ExtendedIMedia,
    isMobile?: boolean
  ): IPushCardWithLayout {
    const { isHob, liveBrand, siteBrand } = this.hobStateService.hobState;
    const theme = ThemesMapper.mapBrandTheme(cmp.theme);
    const isValidTheme = theme === ThemesEnum.light || theme === ThemesEnum.dark;
    const themeHob = isValidTheme ? theme : ThemesEnum.light;
    const themesConfig = isHob
      ? themeHob
      : theme;

    return {
      push: mediaContainer ? this.toPushCard(cmp, mediaContainer, isMobile) : this.toPushCard(cmp),
      imageRatio: ImageRatioMapper.mapImageRatio(cmp.imageRatio),
      textConfig: ImageRatioMapper.mapPosition(cmp.textConfig) as
        | PositionEnum.center
        | PositionEnum.left
        | PositionEnum.right,
      mediaConfig: ImageRatioMapper.mapPosition(cmp.mediaConfig),
      mediaAsBackground: cmp.mediaConfig === PushCardMediaConfigEnum.BACKGROUND,
      themesConfig: themesConfig,
      titleLevel: transformHeadlineLevelToTitleLevel(cmp.headlineLevel),
      indexFollow: cmp?.indexFollow,
      brand: cmp?.brandCorner,
      isDescription: cmp?.isDescription === 'true',
      isFit: cmp?.isFit === 'true',
      hasModal: cmp.hasModal === 'true',
      brandBar: cmp?.brandBar
        ?? (isHob && mediaContainer !== undefined
          ? liveBrand || siteBrand
          : undefined)
    };
  }

  categoryAdditionalMarketingDatatoIPushCard(
    data: CategoryAdditionalMarketingData,
    customMedia?: CustomMedia,
    brandLogo?: IBrandLogo
  ): IPushCard {
    const externalLink = data.target
      ? data.target === CategoryAdditionalMarketingDataTargetEnum.In_new_window
      : undefined;
    const media = customMedia || data.media;
    const richParagraphData = {
      heading: {
        headline: data.headline,
        subline: data.subline,
        description: data.description,
        brandLogo: brandLogo?.media ? {
          media: brandLogo?.media,
          position: PositionEnum.bottom
        } : null
      }
    };
    return {
      richParagraph: {
        footnote: data.footnote,
        links: !!data.urlLink && data.linkName ? [LinkMapper.toLink(data.urlLink, data.linkName, externalLink)] : [],
        heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData),
        tags: []
      },
      media: this.imageService.normalizeMedia(media)
    };
  }

  categoryAdditionalMarketingDatatoISeoParagraph(data: CategoryAdditionalMarketingData): ISeoParagraph {
    const media = data.mediaContainer
      ? this.imageService.getMediaFromCustomMediaArray(data.mediaContainer)
      : data.media;
    return {
      heading: toHeading({ headline: data.headline, subline: data.descriptionWysiwyg }),
      media: this.imageService.normalizeMedia(media),
      mediaPosition: data?.mediaPosition?.toLowerCase() as PositionEnum
    };
  }

  toMiniPushCard(
    cmp: PushCardComponentInterface,
    mediaContainer?: ExtendedIMedia,
    isMobile?: boolean
  ): IMiniPushCard {
    return {
      media: this.getMediaContent(cmp, mediaContainer, isMobile),
      richParagraph: this.getRichParagraph(cmp)
    };
  }

  createTrackingData(
    cmp: PushCardComponentInterface,
    position: number,
    contentName: string,
    productCode?: string,
    productName?: string
  ): IPushCardTracking {
    return {
      carouselAction: cmp?.trackingAction || 'Redirect',
      position,
      contentName,
      productID: productCode || '',
      productName: productName || ''
    };
  }

  private getMediaContent(
    cmp: PushCardComponentInterface,
    mediaContainer?: ExtendedIMedia,
    isMobile?: boolean
  ): any {
    if (!mediaContainer && !cmp.media) {
      return null;
    }

    if (mediaContainer && cmp.mediaContainer) {
      const mediaSource = isMobile && cmp.mediaContainer?.mobile
        ? cmp.mediaContainer.mobile
        : cmp.mediaContainer.desktop;
      return this.imageService.normalizeMedia(mediaSource);
    }

    return this.imageService.normalizeMedia(cmp.media);
  }

  private getRichParagraph(cmp: PushCardComponentInterface) {
    return {
      footnote: cmp.footnote,
      links: cmp.linksObjects
        ? cmp.linksObjects
          .filter((link) => !link.media)
          .map((link) => LinkMapper.cmsLinkToILink(link))
        : [],
      linksImages: cmp.linksObjects
        ? cmp.linksObjects
          .filter((link) => !!link.media)
          .map((link) => this.linkMapper.toLinkMedia(link))
        : [],
      heading: this.headingParagraphMapper.toHeadingParagraph(cmp),
      tags: cmp?.tagsObjects?.map((tag) => ({
        text: tag?.label,
        theme: ThemesMapper.mapBrandTheme(tag.theme)
      })) ?? [],
      details: {
        title: cmp?.tooltipTitle,
        description: cmp?.tooltipDescription
      }
    };
  }

  private toPushCard(
    cmp: PushCardComponentInterface,
    mediaContainer?: ExtendedIMedia,
    isMobile?: boolean
  ): IPushCard {
    return {
      media: this.getMediaContent(cmp, mediaContainer, isMobile),
      richParagraph: this.getRichParagraph(cmp),
      isPromo: cmp?.isPushPromo === 'true'
    };
  }
}
